import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import JSZip from "jszip";
import { CircularProgress } from "@mui/material";
import apiClient from "../api/apiClient";
import ProgressModal from "../components/ProgressModal";
import { getSafeExportErrorCategory, getTemplateAnalyticsParams, trackEvent } from "../analytics";
import { createEngine, DEFAULT_APPEARANCE, lightingAvailability } from "../mockup-engine";
import { openForExport, useMockupTemplate } from "./useMockupTemplate";
import MockupCanvas from "./MockupCanvas";
import { AppearanceSection, PlacementSection, Tip } from "./StudioControls";
import DiscoverPanel from "./DiscoverPanel";
import ProductDialog from "./ProductDialog";
import LibraryDialog from "./LibraryDialog";
import DownloadMenu from "./DownloadMenu";
import { useDesignThumbnail } from "./designThumbnails";
import { BackIcon, ChevronLeftIcon, ChevronRightIcon, CloseIcon, GridIcon, InfoIcon, PlusIcon, SparkIcon } from "./icons";
import { align, centeredPlacement, denormalizePlacement, normalizePlacement, scaledSize } from "./placement";
import { cleanFileName, downloadBlob, exportDesign, loadDesignFile, randomSuffix } from "./exporter";
import { MAX_MOCKUPS, listingAccessAllowed, listingImage, listingPreviewImage, listingTitle } from "./listings";
import "./studio.css";

const MAX_DESIGNS = 30;
const MAX_DESIGNS_MOBILE = 4;
const ACCEPT = "image/png,image/jpeg,image/webp,image/gif,image/avif,image/svg+xml";
const PANELS_KEY = "mockee.studio.panels";
const isMobileWidth = () => typeof window !== "undefined" && window.innerWidth < 768;

let nextDesignId = 1;

function readPanels() {
  try {
    return { settings: false, discover: false, ...JSON.parse(window.localStorage.getItem(PANELS_KEY) || "{}") };
  } catch {
    return { settings: false, discover: false };
  }
}

/**
 * The mockup editor: designs on one or more mockups (up to 10), with
 * Placement / Appearance controls, a live preview and Discover.
 *
 * - initialListingId: open this listing first (/mockup-editor?id=…)
 * - initialListing:   an already loaded listing (shared editor links)
 * - shared:           a single shared mockup; no Discover or other mockups
 */
export default function StudioWorkspace({ initialListingId = null, initialListing = null, shared = false, title = "Mockup Editor" }) {
  const navigate = useNavigate();
  const { role, userid } = useSelector((state) => state.auth);
  const isAdmin = role === "A";
  const canAccess = useCallback((detail) => (shared ? !!detail : listingAccessAllowed(detail, { isAdmin, userid })), [shared, isAdmin, userid]);

  // --- Mockups ---------------------------------------------------------------
  const [mockups, setMockups] = useState([]); // [{ id, listing }]
  const [activeId, setActiveId] = useState(null);
  const [details, setDetails] = useState({}); // id -> listing detail
  const [initialError, setInitialError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const detailsRef = useRef(details);
  detailsRef.current = details;

  // --- Designs and per-mockup state ------------------------------------------
  const [designs, setDesigns] = useState([]);
  const [activeDesignId, setActiveDesignId] = useState(null);
  const [designForMockup, setDesignForMockup] = useState({}); // mockupId -> designId
  const [placements, setPlacements] = useState({}); // "mockupId:designId" -> { placement, mode, w, h }
  const [sharedPlacement, setSharedPlacement] = useState(null); // normalized, from Apply to all
  const [appearances, setAppearances] = useState({}); // mockupId -> appearance

  // --- UI ----------------------------------------------------------------------
  const [panels, setPanels] = useState(readPanels);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [discoverSearch, setDiscoverSearch] = useState("");
  const [grid, setGrid] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const [product, setProduct] = useState(null); // { listing, detailsOnly }
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [isMobile, setIsMobile] = useState(isMobileWidth);
  const batchCancelRef = useRef(false);
  const trackedStarts = useRef(new Set());
  const searchRef = useRef(null);
  const dropDepth = useRef(0);

  useEffect(() => {
    const onResize = () => setIsMobile(isMobileWidth());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const setPanel = (key, collapsed) =>
    setPanels((p) => {
      const next = { ...p, [key]: collapsed };
      try {
        window.localStorage.setItem(PANELS_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable */
      }
      return next;
    });

  // --- Active mockup -------------------------------------------------------------
  const detail = activeId ? details[activeId] : null;
  const accessOk = canAccess(detail);
  const { phase, template, progress, label, error, info, draft, exportReady, prepareExport } = useMockupTemplate(accessOk ? detail : null, reloadKey);
  const ready = phase === "ready";
  const designW = template?.designWidth || info?.designWidth || 0;
  const designH = template?.designHeight || info?.designHeight || 0;
  const analytics = useMemo(() => getTemplateAnalyticsParams(activeId, detail), [activeId, detail]);
  const availability = useMemo(() => (template ? lightingAvailability(template) : null), [template]);
  const appearance = (activeId && appearances[activeId]) || DEFAULT_APPEARANCE;
  const maxDesigns = isMobile ? MAX_DESIGNS_MOBILE : MAX_DESIGNS;
  const inEditor = useMemo(() => new Set(mockups.map((m) => m.id)), [mockups]);

  useEffect(() => {
    if (ready && activeId && !trackedStarts.current.has(activeId)) {
      trackedStarts.current.add(activeId);
      trackEvent("editor_start", analytics);
    }
  }, [ready, activeId, analytics]);

  const fetchDetail = useCallback(async (id) => {
    if (detailsRef.current[id]) return detailsRef.current[id];
    const result = await apiClient.get(`/listings/${id}/`);
    if (!result.ok) return null;
    setDetails((d) => ({ ...d, [id]: result.data }));
    return result.data;
  }, []);

  const activate = useCallback(
    (id) => {
      setActiveId(id);
      const remembered = designForMockup[id];
      if (remembered && designs.some((d) => d.id === remembered)) setActiveDesignId(remembered);
    },
    [designForMockup, designs],
  );

  // First mockup: from the page (id or shared listing).
  useEffect(() => {
    if (initialListing) {
      const id = String(initialListing.id);
      setDetails((d) => ({ ...d, [id]: initialListing }));
      setMockups([{ id, listing: initialListing }]);
      setActiveId(id);
      return undefined;
    }
    if (!initialListingId) return undefined;
    let cancelled = false;
    const id = String(initialListingId);
    apiClient.get(`/listings/${id}/`).then((result) => {
      if (cancelled) return;
      if (!result.ok) {
        setInitialError("This mockup could not be found.");
        return;
      }
      setDetails((d) => ({ ...d, [id]: result.data }));
      setMockups((list) => (list.some((m) => m.id === id) ? list : [{ id, listing: result.data }, ...list].slice(0, MAX_MOCKUPS)));
      setActiveId((current) => current ?? id);
    });
    return () => {
      cancelled = true;
    };
  }, [initialListingId, initialListing]);

  /** Adds a listing to the editor (or shows it when it is already there). */
  const addListing = useCallback(
    async (listing, { activateIt = true } = {}) => {
      const id = String(listing.id);
      if (inEditor.has(id)) {
        if (activateIt) activate(id);
        return true;
      }
      if (mockups.length >= MAX_MOCKUPS) {
        toast.info(`You can have up to ${MAX_MOCKUPS} mockups in the editor. Remove one to add another.`);
        return false;
      }
      const d = await fetchDetail(id);
      if (!d) {
        toast.error("Could not load this mockup. Try again.");
        return false;
      }
      if (!canAccess(d)) {
        setProduct({ listing: d });
        return false;
      }
      setMockups((list) => (list.some((m) => m.id === id) || list.length >= MAX_MOCKUPS ? list : [...list, { id, listing: d }]));
      if (activateIt) activate(id);
      return true;
    },
    [inEditor, mockups.length, fetchDetail, canAccess, activate],
  );

  const removeMockup = (id) => {
    if (mockups.length <= 1) {
      toast.info("Keep at least one mockup in your editor.");
      return;
    }
    const index = mockups.findIndex((m) => m.id === id);
    const remaining = mockups.filter((m) => m.id !== id);
    setMockups(remaining);
    if (activeId === id) activate(remaining[Math.min(index, remaining.length - 1)].id);
    setPlacements((p) => Object.fromEntries(Object.entries(p).filter(([k]) => !k.startsWith(`${id}:`))));
    setAppearances((a) => omit(a, id));
    setDesignForMockup((m) => omit(m, id));
  };

  // --- Designs ---------------------------------------------------------------------
  const design = designs.find((d) => d.id === activeDesignId) || null;

  // One design always stays selected while any exist.
  useEffect(() => {
    if (designs.length && !designs.some((d) => d.id === activeDesignId)) setActiveDesignId(designs[0].id);
  }, [designs, activeDesignId]);

  const designsRef = useRef(designs);
  designsRef.current = designs;
  useEffect(() => () => designsRef.current.forEach(releaseDesign), []);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/") || /\.(png|jpe?g|webp|gif|avif|svg)$/i.test(f.name));
    if (!files.length) return;
    const room = maxDesigns - designs.length;
    if (room <= 0) {
      toast.info(`You can upload up to ${maxDesigns} designs${isMobile ? " on mobile" : ""}.`);
      return;
    }
    if (files.length > room) toast.info(`Only the first ${room} design${room === 1 ? "" : "s"} were added (limit ${maxDesigns}).`);
    let first = null;
    for (const file of files.slice(0, room)) {
      try {
        const loaded = await loadDesignFile(file);
        const entry = { id: nextDesignId++, name: file.name, ...loaded, thumb: thumbnailOf(loaded.texture) };
        setDesigns((list) => [...list, entry]);
        if (!first) {
          first = entry;
          setActiveDesignId(entry.id);
          if (activeId) setDesignForMockup((m) => ({ ...m, [activeId]: entry.id }));
        }
        trackEvent("design_upload_success", analytics);
      } catch (e) {
        toast.error(e.message || `Could not read “${file.name}”.`);
      }
    }
  };

  const selectDesign = (id) => {
    setActiveDesignId(id);
    if (activeId) setDesignForMockup((m) => ({ ...m, [activeId]: id }));
  };

  const removeDesign = (id) => {
    const index = designs.findIndex((d) => d.id === id);
    const removed = designs[index];
    const remaining = designs.filter((d) => d.id !== id);
    setDesigns(remaining);
    if (activeDesignId === id) setActiveDesignId(remaining[Math.min(index, remaining.length - 1)]?.id ?? null);
    setPlacements((p) => Object.fromEntries(Object.entries(p).filter(([k]) => !k.endsWith(`:${id}`))));
    setDesignForMockup((m) => Object.fromEntries(Object.entries(m).filter(([, d]) => d !== id)));
    if (removed) setTimeout(() => releaseDesign(removed), 0);
  };

  // --- Placement ---------------------------------------------------------------------
  /** Placement of a design on a mockup with a design area of w × h. */
  const placementFor = useCallback(
    (mockupId, d, w, h) => {
      if (!d || !w || !h) return null;
      const own = placements[`${mockupId}:${d.id}`];
      if (own) {
        if (own.w === w && own.h === h) return own;
        // Same mockup at another resolution (quick draft → full template).
        const n = normalizePlacement(own.placement, own.mode, own.w, own.h, d.naturalWidth, d.naturalHeight);
        return { placement: denormalizePlacement(n, w, h, d.naturalWidth, d.naturalHeight), mode: own.mode };
      }
      if (sharedPlacement) return { placement: denormalizePlacement(sharedPlacement, w, h, d.naturalWidth, d.naturalHeight), mode: sharedPlacement.mode };
      return { placement: centeredPlacement(w, h, d.naturalWidth, d.naturalHeight, "fit"), mode: "fit" };
    },
    [placements, sharedPlacement],
  );

  const current = design && activeId ? placementFor(activeId, design, designW, designH) : null;
  const activeDesign = design && current ? { ...design, ...current } : null;
  const natural = design && designW ? scaledSize(designW, designH, design.naturalWidth, design.naturalHeight, "fit") : null;

  // Discover and My mockups cards show the active design as it would be
  // placed when that mockup is added: the Apply to all placement, else Fit.
  const cardDesign = useMemo(() => {
    if (!design) return null;
    return {
      key: `${design.id}:${sharedPlacement ? JSON.stringify(sharedPlacement) : "fit"}`,
      texture: design.texture,
      placementFor: (w, h) =>
        sharedPlacement
          ? denormalizePlacement(sharedPlacement, w, h, design.naturalWidth, design.naturalHeight)
          : centeredPlacement(w, h, design.naturalWidth, design.naturalHeight, "fit"),
    };
  }, [design, sharedPlacement]);

  /** Design and placement of a mockup in the strip, for its thumbnail. */
  const tileDesign = (mockupId) => {
    const d = designs.find((x) => x.id === designForMockup[mockupId]) || design;
    if (!d) return null;
    const own = placements[`${mockupId}:${d.id}`];
    return {
      key: `${d.id}:${JSON.stringify(own ? [own.placement, own.w, own.h] : sharedPlacement || "fit")}`,
      texture: d.texture,
      placementFor: (w, h) => placementFor(mockupId, d, w, h)?.placement,
    };
  };

  const setCurrent = (patch) => {
    if (!activeId || !design || !current) return;
    setPlacements((p) => ({ ...p, [`${activeId}:${design.id}`]: { ...current, ...patch, w: designW, h: designH } }));
  };
  const onPlacementChange = useCallback(
    (placement) => {
      setInteracted(true);
      setCurrent({ placement });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeId, design, current, designW, designH],
  );
  const onPlacementCommit = useCallback(
    (placement) => setCurrent({ placement, mode: "custom" }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeId, design, current, designW, designH],
  );
  const fit = (mode) => design && designW && setCurrent({ placement: centeredPlacement(designW, designH, design.naturalWidth, design.naturalHeight, mode), mode });
  const alignTo = (edge) => current && setCurrent({ placement: align(current.placement, designW, designH, edge), mode: "custom" });

  const applyToAll = () => {
    if (!current || !design) return;
    const before = { placements, sharedPlacement };
    setSharedPlacement(normalizePlacement(current.placement, current.mode, designW, designH, design.naturalWidth, design.naturalHeight));
    setPlacements({});
    toast(
      ({ closeToast }) => (
        <div className="sw-toast-undo">
          <span>Placement applied to all designs and mockups</span>
          <button
            type="button"
            onClick={() => {
              setPlacements(before.placements);
              setSharedPlacement(before.sharedPlacement);
              closeToast();
              toast.info("Previous placements restored");
            }}
          >
            Undo
          </button>
        </div>
      ),
      { autoClose: 15000 },
    );
  };

  const setAppearance = (next) => activeId && setAppearances((a) => ({ ...a, [activeId]: next }));

  // --- Export --------------------------------------------------------------------------
  const recordDownload = async (blob, fileName, listingId) => {
    if (!userid) return true;
    const formData = new FormData();
    formData.append("image", blob, fileName);
    formData.append("user", userid);
    formData.append("listing", listingId);
    const result = await apiClient.post("/downloads/", formData);
    if (!result.ok) console.error(`Download record failed for listing ${listingId}`, result.data);
    return result.ok;
  };

  const baseName = (d, fallback) => cleanFileName(d?.product_name || d?.title || fallback || "mockup");

  const downloadCurrent = async () => {
    if (!activeDesign || !template || downloading) return;
    setDownloading(true);
    try {
      const engine = await prepareExport();
      const blob = await exportDesign(engine, "full", template, activeDesign, appearance);
      const fileName = `${baseName(detail)}_${randomSuffix()}.jpeg`;
      if (!(await recordDownload(blob, fileName, activeId))) {
        toast.error("Could not save this download to your account. Try again.");
        trackEvent("mockup_export_failed", { ...analytics, export_mode: "single", error_category: "download_recording" });
        return;
      }
      downloadBlob(blob, fileName);
      trackEvent("mockup_export_success", { ...analytics, export_mode: "single" });
    } catch (e) {
      console.error("Mockup download failed", e);
      trackEvent("mockup_export_failed", { ...analytics, export_mode: "single", error_category: getSafeExportErrorCategory(e) });
      toast.error("Oops! Something went wrong. Try again...");
    } finally {
      setDownloading(false);
    }
  };

  /** Renders a list of jobs into a ZIP: [{ listingId, detail, design, appearance }]. */
  const downloadZip = async (jobs, zipName, mode) => {
    if (batch || jobs.length < 2) return;
    batchCancelRef.current = false;
    setBatch({ progress: 0, title: "Preparing your downloads…" });
    const zip = new JSZip();
    const batchEngine = createEngine();
    let exported = 0;
    try {
      for (const [index, job] of jobs.entries()) {
        if (batchCancelRef.current) break;
        const step = (f) => setBatch((b) => b && { ...b, progress: Math.round(((index + Math.min(1, f)) / jobs.length) * 100) });
        let engine, docId, dims;
        if (job.listingId === activeId && template) {
          engine = await prepareExport((p) => step(p * 0.5));
          docId = "full";
          dims = template;
        } else {
          engine = batchEngine;
          docId = `batch-${job.listingId}`;
          dims = await openForExport(batchEngine, docId, job.detail, (p) => step(p * 0.5));
        }
        const placed = placementFor(job.listingId, job.design, dims.designWidth, dims.designHeight);
        const blob = await exportDesign(engine, docId, dims, { ...job.design, ...placed }, job.appearance, (p) => step(0.5 + p * 0.5));
        if (engine === batchEngine) batchEngine.close(docId).catch(() => {});
        const fileName = `${String(index + 1).padStart(2, "0")}_${baseName(job.detail, `mockup_${index + 1}`)}_${cleanFileName(job.design.name.replace(/\.[^/.]+$/, ""))}.jpeg`;
        zip.file(fileName, blob);
        await recordDownload(blob, fileName, job.listingId).catch(() => false);
        exported++;
        step(1);
      }
      if (batchCancelRef.current) {
        toast.info("Download cancelled.");
        return;
      }
      const content = await zip.generateAsync({ type: "blob", compression: "STORE" });
      downloadBlob(content, zipName);
      trackEvent("mockup_export_success", { ...analytics, export_mode: mode, template_count: exported });
      toast.success(`${exported} mockups downloaded.`);
    } catch (e) {
      console.error("Download all failed", e);
      trackEvent("mockup_export_failed", { ...analytics, export_mode: mode, template_count: jobs.length, error_category: getSafeExportErrorCategory(e) });
      toast.error("Could not create the zip file. Try again in a moment.");
    } finally {
      batchEngine.dispose();
      setBatch(null);
    }
  };

  const downloadAllDesigns = () =>
    detail &&
    downloadZip(
      designs.map((d) => ({ listingId: activeId, detail, design: d, appearance })),
      `${baseName(detail)}_designs.zip`,
      "all",
    );

  const mockupJobs = mockups
    .map((m) => {
      const d = details[m.id];
      const chosen = designs.find((x) => x.id === designForMockup[m.id]) || design || designs[0];
      return d && canAccess(d) && chosen ? { listingId: m.id, detail: d, design: chosen, appearance: appearances[m.id] || DEFAULT_APPEARANCE } : null;
    })
    .filter(Boolean);
  const downloadAllMockups = () => downloadZip(mockupJobs, "mockee_mockups.zip", "multi_template_zip");

  const downloadOptions = [
    { key: "current", label: "Current mockup · JPEG", help: "Download the active mockup with its current design and settings", disabled: !activeDesign || !template, onSelect: downloadCurrent },
    {
      key: "designs",
      label: "All designs on this mockup · ZIP",
      detail: `${designs.length} design${designs.length === 1 ? "" : "s"}`,
      help: "Download this mockup once per uploaded design",
      disabled: designs.length < 2 || !template,
      onSelect: downloadAllDesigns,
    },
  ];
  if (!shared) {
    downloadOptions.push({
      key: "mockups",
      label: "All mockups · ZIP",
      detail: `${mockupJobs.length} mockup${mockupJobs.length === 1 ? "" : "s"}`,
      help: "Download one image per mockup in the editor, using each mockup's selected design",
      disabled: mockupJobs.length < 2,
      onSelect: downloadAllMockups,
    });
  }

  // --- Discover / dialogs ------------------------------------------------------------------
  const openDiscover = () => {
    if (window.matchMedia?.("(max-width: 1150px)").matches) setDrawerOpen(true);
    else setPanel("discover", false);
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (shared) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        openDiscover();
      } else if (e.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shared]);

  const handleProductAdd = async (listing) => {
    if (await addListing(listing)) {
      setProduct(null);
      setDrawerOpen(false);
    }
  };

  const handlePurchased = async (fresh) => {
    const id = String(fresh.id);
    setDetails((d) => ({ ...d, [id]: fresh }));
    detailsRef.current = { ...detailsRef.current, [id]: fresh };
    setProduct(null);
    if (inEditor.has(id)) {
      activate(id);
      toast.success("Purchase complete. Your mockup is ready to edit.");
      return;
    }
    if (mockups.length >= MAX_MOCKUPS) {
      toast.success("Purchase complete. Remove a mockup from the editor to add it.");
      return;
    }
    setMockups((list) => (list.some((m) => m.id === id) ? list : [...list, { id, listing: fresh }]));
    activate(id);
    setDrawerOpen(false);
    toast.success("Purchase complete. The mockup was added to your editor.");
  };

  const addMany = async (listings) => {
    setAdding(true);
    try {
      let added = 0;
      for (const listing of listings) if (await addListing(listing, { activateIt: added === 0 })) added++;
      if (added) toast.success(`${added} mockup${added === 1 ? "" : "s"} added to your editor.`);
      setLibraryOpen(false);
    } finally {
      setAdding(false);
    }
  };

  // --- Design drop target -------------------------------------------------------------------
  const dropHandlers = {
    onDragEnter: (e) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
      dropDepth.current++;
      setDropActive(true);
    },
    onDragOver: (e) => {
      if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
    },
    onDragLeave: () => {
      if (--dropDepth.current <= 0) {
        dropDepth.current = 0;
        setDropActive(false);
      }
    },
    onDrop: (e) => {
      e.preventDefault();
      dropDepth.current = 0;
      setDropActive(false);
      if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    },
  };

  // --- Render ----------------------------------------------------------------------------
  const busy = !!batch || downloading;
  const showCanvas = !!(template && activeDesign && accessOk);
  const layoutClass = [
    "sw-layout",
    shared ? "is-shared" : "",
    panels.settings ? "settings-collapsed" : "",
    panels.discover && !shared ? "discover-collapsed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="sw-studio">
      <input
        id="sw-design-upload"
        type="file"
        accept={ACCEPT}
        multiple
        className="sw-file-input"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <div className="sw-toolbar">
        <button type="button" className="sw-icon-button" onClick={() => navigate(-1)} aria-label="Go back">
          <BackIcon />
        </button>
        <h1>{title}</h1>
        <div className="sw-toolbar-actions">
          {!shared && (
            <button type="button" className="sw-button sw-drawer-toggle" onClick={openDiscover}>
              Browse mockups
            </button>
          )}
          <DownloadMenu options={downloadOptions} busy={busy} disabled={!designs.length} />
        </div>
      </div>

      <div className={layoutClass}>
        {/* Placement + Appearance */}
        <aside className={`sw-panel sw-settings${panels.settings ? " is-collapsed" : ""}`} aria-label="Adjustments">
          {panels.settings ? (
            <button type="button" className="sw-folded" onClick={() => setPanel("settings", false)} aria-expanded="false" aria-label="Expand adjustments">
              <ChevronRightIcon />
              <span>Adjustments</span>
            </button>
          ) : (
            <>
              <div className="sw-settings-section sw-placement-section">
                <PlacementSection
                  design={activeDesign}
                  designWidth={designW}
                  designHeight={designH}
                  onAlign={alignTo}
                  onFit={fit}
                  onReset={() => fit("fit")}
                  onChange={onPlacementChange}
                  onCommit={onPlacementCommit}
                  onApplyAll={applyToAll}
                  applyAllDisabled={!activeDesign || (designs.length < 2 && mockups.length < 2)}
                  applyAllHint={designs.length < 2 && mockups.length < 2 ? "Add more designs or mockups to copy this placement" : "Copy this placement to every design and mockup in the editor"}
                  headingAction={
                    <button type="button" className="sw-collapse-button" onClick={() => setPanel("settings", true)} aria-expanded="true" aria-label="Collapse adjustments">
                      <ChevronLeftIcon />
                    </button>
                  }
                />
              </div>
              <div className="sw-settings-section sw-appearance-section">
                <AppearanceSection appearance={appearance} onChange={setAppearance} availability={availability} disabled={!template || !accessOk} />
              </div>
            </>
          )}
        </aside>

        {/* Designs */}
        <section className={`sw-panel sw-rail sw-designs${dropActive ? " is-drop-active" : ""}`} aria-label="Designs" {...dropHandlers}>
          <div className="sw-panel-heading">
            <h2>Designs</h2>
            <span className="sw-count" title={`Maximum ${maxDesigns} designs`}>
              {designs.length}/{maxDesigns}
            </span>
          </div>
          <div className="sw-rail-list">
            {designs.map((d, i) => (
              <div key={d.id} className={`sw-tile${d.id === activeDesignId ? " is-selected" : ""}`}>
                <Tip title="Use this design on the active mockup" placement="right">
                  <button type="button" className="sw-tile-select" onClick={() => selectDesign(d.id)} aria-pressed={d.id === activeDesignId} aria-label={`Use design ${i + 1}: ${d.name}`}>
                    <img src={d.thumb} alt="" />
                  </button>
                </Tip>
                <button type="button" className="sw-tile-remove" onClick={() => removeDesign(d.id)} aria-label={`Delete design ${i + 1}`} title="Delete this design from the session">
                  <CloseIcon />
                </button>
              </div>
            ))}
            {designs.length < maxDesigns && (
              <Tip title="Upload designs to try on your mockups (or drop images here)" placement="right">
                <label htmlFor="sw-design-upload" className="sw-tile sw-tile-add" tabIndex={0} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && document.getElementById("sw-design-upload")?.click()}>
                  <PlusIcon />
                  <span className="sw-visually-hidden">Upload designs</span>
                </label>
              </Tip>
            )}
          </div>
          {!designs.length && <p className="sw-rail-note">PNG, JPG or WebP</p>}
        </section>

        {/* Preview */}
        <section className={`sw-panel sw-preview${interacted ? " has-interacted" : ""}`} aria-label="Preview">
          <div className="sw-preview-toolbar">
            <h2>Preview</h2>
            <span className="sw-status" role="status">
              {phase === "draft" && "Quick preview · loading full quality…"}
              {ready && template && !exportReady && "Preparing full resolution…"}
            </span>
            <div className="sw-preview-actions">
              <Tip title={grid ? "Hide the design grid" : "Show a grid on the design to check the surface"}>
                <button type="button" className={`sw-icon-button is-plain${grid ? " is-on" : ""}`} onClick={() => setGrid((g) => !g)} aria-pressed={grid} disabled={!showCanvas} aria-label="Show grid">
                  <GridIcon />
                </button>
              </Tip>
              <Tip title="View mockup details and design dimensions">
                <button type="button" className="sw-icon-button is-plain" onClick={() => detail && setProduct({ listing: detail, detailsOnly: true })} disabled={!detail} aria-label="Mockup details">
                  <InfoIcon />
                </button>
              </Tip>
            </div>
          </div>
          <div className="sw-stage" style={template ? { "--sw-aspect": `${template.width} / ${template.height}` } : undefined}>
            {showCanvas ? (
              <MockupCanvas
                template={template}
                artwork={activeDesign.texture}
                placement={activeDesign.placement}
                natural={natural}
                appearance={appearance}
                grid={grid}
                onChange={onPlacementChange}
                onCommit={onPlacementCommit}
              />
            ) : (
              <StageMessage
                mockups={mockups}
                detail={detail}
                accessOk={accessOk}
                phase={phase}
                progress={progress}
                label={label}
                error={error || initialError}
                hasDesign={!!design}
                shared={shared}
                onBrowse={openDiscover}
                onBuy={() => detail && setProduct({ listing: detail })}
                onRetry={() => setReloadKey((k) => k + 1)}
              />
            )}
          </div>
          <p className="sw-preview-footer">{showCanvas ? "Drag to move · corners to resize · round handle to rotate (Shift snaps to 15°)" : draft ? "Quick preview" : ""}</p>
        </section>

        {/* Mockups */}
        <section className="sw-panel sw-rail sw-mockups" aria-label="Mockups in the editor">
          <div className="sw-panel-heading">
            <h2>Mockups</h2>
            <span className="sw-count">
              {mockups.length}/{MAX_MOCKUPS}
            </span>
          </div>
          <div className="sw-rail-list">
            {mockups.map((m) => {
              const d = details[m.id] || m.listing;
              const locked = details[m.id] && !canAccess(details[m.id]);
              return (
                <div key={m.id} className={`sw-tile sw-mockup-tile${m.id === activeId ? " is-selected" : ""}${locked ? " is-locked" : ""}`}>
                  <Tip title={`Switch to this mockup: ${listingTitle(d)}${locked ? " (not owned yet)" : ""}`} placement="left">
                    <button type="button" className="sw-tile-select" onClick={() => activate(m.id)} aria-pressed={m.id === activeId} aria-label={`Edit mockup ${listingTitle(d)}`} disabled={busy}>
                      <MockupTileImage listing={d} design={tileDesign(m.id)} />
                    </button>
                  </Tip>
                  {!shared && (
                    <button type="button" className="sw-tile-remove" onClick={() => removeMockup(m.id)} aria-label={`Remove mockup ${listingTitle(d)}`} title="Remove this mockup from the editor" disabled={busy}>
                      <CloseIcon />
                    </button>
                  )}
                </div>
              );
            })}
            {!shared && mockups.length < MAX_MOCKUPS && (
              <Tip title="Browse and add your mockups" placement="left">
                <button type="button" className="sw-tile sw-tile-add" onClick={() => setLibraryOpen(true)} aria-label="Add mockups from My mockups" disabled={busy}>
                  <PlusIcon />
                </button>
              </Tip>
            )}
          </div>
        </section>

        {/* Discover */}
        {!shared && (
          <DiscoverPanel
            creatorId={detail?.user_id || null}
            inEditor={inEditor}
            design={cardDesign}
            onOpen={(listing) => setProduct({ listing })}
            search={discoverSearch}
            onSearch={setDiscoverSearch}
            searchRef={searchRef}
            collapsed={panels.discover && !drawerOpen}
            onCollapse={() => setPanel("discover", true)}
            onExpand={() => setPanel("discover", false)}
            drawerOpen={drawerOpen}
            onCloseDrawer={() => setDrawerOpen(false)}
          />
        )}
        {drawerOpen && <div className="sw-drawer-backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />}
      </div>

      {product && (
        <ProductDialog
          listing={product.listing}
          detailsOnly={product.detailsOnly}
          template={String(product.listing.id) === activeId ? template : null}
          inEditor={inEditor.has(String(product.listing.id))}
          mockupCount={mockups.length}
          onClose={() => setProduct(null)}
          onAdd={handleProductAdd}
          onPurchased={handlePurchased}
        />
      )}
      {libraryOpen && (
        <LibraryDialog
          inEditor={inEditor}
          mockupCount={mockups.length}
          busy={adding}
          design={cardDesign}
          onClose={() => setLibraryOpen(false)}
          onAddMany={addMany}
          onDetails={(listing) => setProduct({ listing, detailsOnly: true })}
        />
      )}
      {batch && (
        <ProgressModal
          isOpen
          progress={batch.progress}
          onCancel={() => {
            batchCancelRef.current = true;
          }}
          title={batch.title}
        />
      )}
    </div>
  );
}

function StageMessage({ mockups, detail, accessOk, phase, progress, label, error, hasDesign, shared, onBrowse, onBuy, onRetry }) {
  if (!mockups.length) {
    if (error) return <Callout error title={error} />;
    if (shared) return <Spinner />;
    return (
      <div className="sw-empty-stage">
        <SparkIcon />
        <h2>A little inspiration, a lot of possibility.</h2>
        <p>Choose a mockup from Discover to start creating.</p>
        <button type="button" className="sw-button sw-primary sw-drawer-toggle-inline" onClick={onBrowse}>
          Browse mockups
        </button>
      </div>
    );
  }
  if (!detail) return <Spinner />;
  const image = listingPreviewImage(detail);
  let callout;
  if (!accessOk) {
    callout = (
      <Callout title="You don't own this mockup yet" text="Buy it to place your designs on it.">
        <button type="button" className="sw-button sw-primary" onClick={onBuy}>
          View & buy
        </button>
      </Callout>
    );
  } else if (phase === "error") {
    callout = (
      <Callout error title="This mockup could not be opened." text={error}>
        <button type="button" className="sw-button" onClick={onRetry}>
          Try again
        </button>
      </Callout>
    );
  } else if (phase === "loading" || phase === "idle") {
    callout = (
      <Callout title={label || "Opening mockup…"}>
        <div className="sw-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
          <span style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
      </Callout>
    );
  } else if (!hasDesign) {
    callout = (
      <Callout title="Add your design" text="Upload an image or drop it on Designs.">
        <label htmlFor="sw-design-upload" className="sw-button sw-primary">
          Upload design
        </label>
      </Callout>
    );
  }
  return (
    <div className="sw-stage-original">
      {image && <img src={image} alt={listingTitle(detail)} />}
      {callout}
    </div>
  );
}

const Spinner = () => (
  <div className="sw-stage-spinner">
    <CircularProgress size={28} sx={{ color: "#c93312" }} />
  </div>
);

function Callout({ title, text, error = false, children }) {
  return (
    <div className={`sw-callout${error ? " is-error" : ""}`} role={error ? "alert" : undefined}>
      <strong>{title}</strong>
      {text && <span>{text}</span>}
      {children}
    </div>
  );
}

function omit(obj, key) {
  const copy = { ...obj };
  delete copy[key];
  return copy;
}

/** Mockups strip image: the mockup with its design once one is chosen. */
function MockupTileImage({ listing, design }) {
  // Debounced: the active mockup's placement changes while dragging.
  const [ref, designSrc] = useDesignThumbnail(listing, design, { delay: 250 });
  return <img ref={ref} src={designSrc || listingImage(listing)} alt="" />;
}

function thumbnailOf(canvas) {
  const size = 200, s = Math.min(1, size / Math.max(canvas.width, canvas.height));
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(canvas.width * s));
  c.height = Math.max(1, Math.round(canvas.height * s));
  c.getContext("2d").drawImage(canvas, 0, 0, c.width, c.height);
  return c.toDataURL("image/png");
}

function releaseDesign(design) {
  // Shrinking canvases frees their backing store immediately (Safari keeps it otherwise).
  for (const c of [design.source, design.texture]) {
    if (c && "width" in c) {
      c.width = 0;
      c.height = 0;
    }
  }
}
